/**
 * The turn a Marvin message draws, as the native engine reports it to Swift (`turn` on
 * `EngineStreamSnapshot` and on each `EngineMessage`): the web's own turn model (`turnModel.ts`)
 * and its rules (`turnPresentation.ts`, `planPresentation.ts`), run here and flattened into plain
 * data so Swift draws it without a second copy of any rule.
 *
 * A turn is its segments, split at each question Marvin asked. A segment is a header and its
 * ordered items: first the step list (narration, steps, and a Deep research turn's plan card),
 * which folds under the header, then the answer's text and its cards in stream order. Text is
 * markdown with its citations already the links native opens, as `EngineSegment` content is.
 *
 * Read tolerantly: Swift skips an item or card kind it does not know, never the message
 * (`MarvinTurn.swift`). The kinds both sides know are listed once, in bridgeManifest.json `turn`.
 */
import { fileReferences, partsFiles, partsReferenceMap } from "../../hooks/streamReducer";
import {
    FULL_PAGE_BOUNDS,
    type ApiBounds,
    type ApiFile,
    type ApiReference,
} from "../../shared/interfaces";
import type { MarvinDigDeeperWidget, MarvinDrawingViewWidget } from "../../shared/marvinWidgets";
import { artifactDisplayName } from "../../util/artifactDisplay";
import { isFullPageBounds } from "../../util/boundsUtil";
import { preprocessMessageMarkdown } from "../../util/markdownStreamUtils";
import { buildRenderItems, type RenderItem } from "../../util/marvinParts";
import { marvinNavigationUrl } from "../../utils/navigation";
import type { ConversationReferenceResolver } from "../conversationRuntime";
import {
    actionMarkdown,
    linkedRenderSegments,
    questionMarkdown,
    referenceHref,
} from "../engineMarkdown";
import type {
    ActivityEntityRef,
    ApiMessagePart,
    ArtifactMeta,
    MarvinAction,
    MarvinNavigationSuggestion,
    MarvinQuestion,
} from "../messageParts";
import { readDrawingPath } from "../referencePolicy";
import {
    ConversationReferenceProcessor,
    resolveThinkingReferences,
    stripBold,
} from "../referenceProjection";
import type { MarvinArtifactPresentation } from "../renderSemantics";
import {
    ACTION_DECISION_ERROR,
    ACTION_INERT_TEXT,
    actionDetails,
    actionOutcomes,
    deepResearchOfferWords,
    draftCardView,
    markupCardView,
    type ActionDetails,
    type DeepResearchOfferWords,
    type DraftCardView,
    type MarkupCardView,
} from "./cardPresentation";
import { planDuration, planRows, shortNoun, type PlanModel } from "./planModel";
import {
    builtText,
    currentAction,
    deepHeaderText,
    deepTailRows,
    groupMeta,
    groupText,
    planSummary,
    waitingRunText,
    workerText,
} from "./planPresentation";
import {
    buildTurnSegments,
    isCountedStep,
    labelParts,
    stepCountText,
    type MarvinOp,
    type StepRef,
    type StepStatus,
    type TurnLane,
    type TurnModel,
    type TurnPhase,
} from "./turnModel";
import {
    CLOCK_FROM_SECONDS,
    HEADER_CLOCK_FORMAT,
    STILL_FROM_SECONDS,
    WAITING_ROW_ID,
    WAITING_TEXT,
    contentBySegment,
    countedSteps,
    currentRowId,
    groupRepeatedSteps,
    headOp,
    headerShown,
    openByDefault,
    rowHelperPhrase,
    rowResult,
    rowText,
    stepWindow,
    stillFormat,
    timesText,
    turnClock,
    turnEndWords,
    turnLead,
    turnRows,
    type GroupedRow,
    type TurnEndWords,
} from "./turnPresentation";

/** The item kinds a segment holds; bridgeManifest.json `turn.items` lists the same. */
export const ENGINE_TURN_ITEM_KINDS = ["narration", "step", "text", "card"] as const;
/** The card kinds an item can carry; bridgeManifest.json `turn.cards` lists the same. */
export const ENGINE_TURN_CARD_KINDS = [
    "artifact",
    "navigation",
    "question",
    "action",
    "markup",
    "deep_research_offer",
    "plan",
    "drawing_view",
] as const;

/** The action type of Marvin's drawing markups (`markup_save`): its card is the markup card. */
const MARKUP_ACTION_TYPE = "markup_save";

/** One chip a reference stands as: its words, and the link that opens it in native when there is one. */
export interface EngineTurnChip {
    label: string;
    /** A viewer (`/doc/...`) or raw-file (`/ref-file?...`) link, as answer citations carry. */
    href?: string;
}

/** A reference a row's words name, drawn as chips (reference chips always). */
export interface EngineTurnRef {
    /** The words of the row this reference stands in. */
    text: string;
    /** A citation of the turn: one chip per reference it names. */
    chips: EngineTurnChip[];
    /** An entity the step names by UUID (an RFI check's sheet, spec, RFI or page); its chip reads `text`. */
    entity?: ActivityEntityRef;
}

/** A row's words split around the references they name, in order (`labelParts`). */
export type EngineTurnLabelPart =
    { kind: "words"; text: string } | { kind: "ref"; ref: EngineTurnRef };

/** Marvin's own sentence before a group of steps: plain words, no icon, not a step. */
export interface EngineTurnNarration {
    kind: "narration";
    id: string;
    text: string;
    label: EngineTurnLabelPart[];
}

export interface EngineTurnStep {
    kind: "step";
    id: string;
    /** The operation icon; `wait` for the row of a question waiting on the user. */
    op: MarvinOp;
    /** The status glyph: done check, failed, stopped, unfinished dash, none while running or waiting. */
    status: StepStatus | "waiting";
    /** The row's words in its state ("Searched drawings for …", "Couldn't finish …"). */
    text: string;
    label: EngineTurnLabelPart[];
    /** "5 sheets": what the finished step found, after " · ". */
    result?: string;
    /** How many identical steps in a row this one stands for (`groupRepeatedSteps`). */
    times: number;
    /** "2 times", after " · ", when it stands for more than one. */
    timesText?: string;
    /** The row in flight: its icon moves, and it reads in the primary colour. */
    current: boolean;
    /** The running row's second line, "Still … · {clock} on this step", once it ran `fromSeconds`. */
    still?: { format: string; fromSeconds: number };
    /** When the engine first saw this row in flight (ms since the epoch, this device's clock). */
    since?: number;
    /** A MiniMarv's row: its name in bold, then its job in words when Marvin gave it one. */
    helper?: { name: string; phrase?: string };
    /** Why a failed step failed, in its tool's words. */
    error?: string;
}

/** One group row of the plan card, in its words. */
export type EngineTurnPlanRow =
    | {
          kind: "group";
          index: number;
          /** "Group 5 · 07 54 19 to 08 42 29". */
          text: string;
          status: "waiting" | "reading" | "done";
          /** "MiniMarv 2", the chip before the text. */
          worker?: string;
          /** What its MiniMarv is opening now. */
          action?: string;
          /** "10 sections · 4 items", "reading · 3 of 10", "waiting". */
          meta: string;
      }
    | { kind: "waiting"; from: number; to: number; text: string; meta: string };

/** A Deep research turn's plan, as the plan card draws it (boards S2 and S3, W-07). */
export interface EngineTurnPlan {
    plan: PlanModel;
    lanes: TurnLane[];
    /** The run is going: the card moves, counts its clock and offers Stop all. */
    live: boolean;
    /** "Deep research" while it runs; "Worked 5 min 40 s", "Stopped 2 min" after. */
    title: string;
    /** "8 of 23 sheets" while it runs (the clock follows); "4 MiniMarvs · 5 groups" after. */
    meta: string;
    /** A live run's clock after `meta`: " · {clock}" from `since` (this device's clock, ms). */
    clock?: { since: number; format: string };
    /** "23 sheets in 5 groups", under the plan sheet's title. */
    scope: string;
    /** "Plan · 136 sections in 14 groups". */
    eyebrow: string;
    /** Items read so far, live (`liveRead`), and "38 of 136 read". */
    read: number;
    readText: string;
    /** The progress bar, 0–100. */
    percent: number;
    rows: EngineTurnPlanRow[];
    /** What a finished turn leaves under its header when folded (`deepTailRows`). */
    tail: Array<{ id: string; text: string; op: "write" | "check"; result: string }>;
}

/** An action card's words: its details as rows and chips, each outcome's line, and a saved draft's card. */
export interface EngineTurnActionView {
    details: ActionDetails;
    /** The line each final status shows (`resultSummary` for the status it has, else the fallback). */
    outcomes: Record<"executed" | "failed" | "rejected" | "expired", string>;
    decisionError: string;
    inertText: string;
    /** An executed RFI draft action's own card ("Draft RFI created"). */
    draft?: DraftCardView;
}

/**
 * A drawing Marvin was asked to show (`drawing_view` widget), drawn in the answer: the place its
 * citation names (a section on a sheet, a whole sheet, a spot on a sheet), as plain data.
 */
export interface EngineTurnDrawingView {
    /** "A-3.2": the sheet's code, bold at the head of the card. */
    code: string;
    /** "Door Schedule": the sheet's and the place's titles after the code (`readDrawingPath`), " · " between. */
    title: string;
    fileId: string;
    /** The page in the file, 0-based. */
    page: number;
    /** The place on the page, as fractions of it from its top-left corner. */
    bounds: { xMin: number; yMin: number; xMax: number; yMax: number };
    /** The place is the whole page: the card draws the sheet alone, with no "where it is" row. */
    wholePage: boolean;
    /** What the citation's chip opens: a viewer (`/doc/...`) or raw-file (`/ref-file?...`) link. */
    href?: string;
    /** The section, when the citation names one. */
    sectionUuid?: string;
}

export type EngineTurnCard =
    | {
          kind: "artifact";
          artifact: ArtifactMeta;
          presentation: MarvinArtifactPresentation;
          /** What the card calls the file (`artifactDisplayName`): its filename, or an
           *  interactive view's title. */
          name: string;
      }
    | { kind: "navigation"; navigation: MarvinNavigationSuggestion }
    | { kind: "question"; question: MarvinQuestion }
    | { kind: "action"; action: MarvinAction; view: EngineTurnActionView }
    | { kind: "markup"; action: MarvinAction; markup?: MarkupCardView }
    | {
          kind: "deep_research_offer";
          offer: MarvinDigDeeperWidget;
          words: DeepResearchOfferWords;
      }
    | { kind: "plan"; plan: EngineTurnPlan }
    | { kind: "drawing_view"; drawing: EngineTurnDrawingView };

export interface EngineTurnCardItem {
    kind: "card";
    id: string;
    card: EngineTurnCard;
    /**
     * The card in words (markdown, as `EngineTurnText.markdown`), for a build that has no native
     * card for its kind yet: what the engine's markdown showed for it before cards. Question and
     * action cards carry one; the others reach native only once a build declares their units.
     */
    fallback?: string;
}

export interface EngineTurnText {
    kind: "text";
    id: string;
    /** Preprocessed markdown, citations as links: render it as an `EngineSegment`'s content. */
    markdown: string;
    /** This is the answer streaming now. */
    streaming: boolean;
}

export type EngineTurnItem =
    EngineTurnNarration | EngineTurnStep | EngineTurnText | EngineTurnCardItem;

export interface EngineTurnHeader {
    /** Whether the header line shows: a finished turn that counted no step has none. */
    shown: boolean;
    /** The head icon, moving while Marvin works; none once the turn finished. */
    op: MarvinOp | null;
    /** "Searching contracts…", "Thinking…", "Writing the answer…", "Needs your answer", the summary, "Stopped". */
    lead: string | null;
    /** The lead's whole words (the running step's label), for its accessibility label. */
    title: string | null;
    /** The lead's tone: what Marvin is doing, a question waiting, a finished summary, a stopped turn. */
    tone: "live" | "waiting" | "done" | "ended";
    stepCount: number;
    /** "3 steps", after the lead and " · ". */
    stepCountText?: string;
    clock: {
        /** The clock counts while Marvin works (it pauses while a question waits). */
        running: boolean;
        /** It shows until the answer starts, or once the turn stopped or dropped… */
        visible: boolean;
        /** …and only after this many seconds. */
        fromSeconds: number;
        /**
         * When the turn started (ms since the epoch, this device's clock): a live turn, and a
         * stopped or dropped one this engine saw run.
         */
        since?: number;
        /** When a stopped or dropped turn ended, on the same clock: its time holds there. */
        endedAt?: number;
        /** " · {clock}": the clock's words after the step count. */
        format: string;
    };
    /** The step list has rows to open. */
    expandable: boolean;
    /** The list is open before the user toggles it (live, before the answer starts); a toggle sticks. */
    open: boolean;
    /** A Deep research turn's header: "Deep research · 38 of 136 sections · 2 min 10 s", with Hide work / Show all work. */
    deep?: { title: string; meta: string };
}

export interface EngineTurnSegment {
    /** Stable for the life of the turn: "segment-0", "segment-1". */
    id: string;
    phase: TurnPhase;
    header: EngineTurnHeader;
    items: EngineTurnItem[];
    /** The first `stepListLength` items are the step list: they fold under the header. */
    stepListLength: number;
    /** A long step list, folded to its newest rows: it shows from `firstShown`, under "Show N earlier steps". */
    window?: { firstShown: number; hidden: number; text: string };
}

/**
 * The notice under a turn that did not finish (`turnEndWords`), and when it ended when this
 * engine saw it end (ms since the epoch, this device's clock): native writes that time into
 * `timed` at `{time}` ("4:34 PM"), else shows `untimed`.
 */
export interface EngineTurnEnd extends TurnEndWords {
    at?: number;
}

export interface EngineTurn {
    phase: TurnPhase;
    segments: EngineTurnSegment[];
    /** A stopped or dropped turn's notice, after its last segment. */
    end?: EngineTurnEnd;
    /** The run that wrote it: what an approval is decided against. */
    runId?: string;
    /** Its conversation: where Deep research is sent and what Stop all stops. */
    conversationId?: string;
    /** The Marvin message that holds it: what a question card's answer names
     *  (`ApiAnsweredQuestion.messageId`). */
    messageId?: string;
}

/**
 * When the engine first saw a live turn and each row in flight, on this device's clock: the
 * header's clock and the "Still …" line count from these. One per run; a saved message has none.
 */
export interface TurnClock {
    readonly startedAt: number;
    readonly rows: Map<string, number>;
    readonly now: () => number;
    /** When the engine first saw the turn finished, stopped or dropped. */
    endedAt?: number;
}

export function createTurnClock(now: () => number = Date.now): TurnClock {
    return { startedAt: now(), rows: new Map(), now };
}

function rowSince(clock: TurnClock, id: string): number {
    const seen = clock.rows.get(id);
    if (seen !== undefined) return seen;
    const at = clock.now();
    clock.rows.set(id, at);
    return at;
}

export interface EngineTurnInput {
    parts: ApiMessagePart[];
    phase: TurnPhase;
    /** The turn's citations, by key, and their files. */
    referenceMap: Record<string, ApiReference>;
    files: ApiFile[];
    resolver?: ConversationReferenceResolver;
    /** A live turn's clock (`createTurnClock`). */
    clock?: TurnClock;
    /** Fold consecutive identical steps into one row (`groupRepeatedSteps`); native does. */
    groupRepeats?: boolean;
    /** The run, conversation and Marvin message the turn belongs to, when known. */
    runId?: string;
    conversationId?: string;
    messageId?: string;
}

interface BuildContext {
    processor: ConversationReferenceProcessor;
    filesById: Map<string, ApiFile>;
    input: EngineTurnInput;
}

function chipsFor(ref: StepRef, context: BuildContext): EngineTurnChip[] {
    return (ref.referenceKeys ?? [])
        .filter((key) => context.processor.isReferenceValid(key))
        .map((key) => {
            const reference = context.processor.getReferenceOrNull(key);
            const href = reference
                ? referenceHref(
                      reference,
                      context.filesById.get(reference.fileId),
                      context.processor.getDescription(key),
                      context.input.resolver,
                  )
                : null;
            const label = context.processor.getText(key).replace(/\s*\|\s*/g, " · ");
            return href ? { label, href } : { label };
        });
}

function labelOf(
    text: string,
    refs: StepRef[] | undefined,
    context: BuildContext,
): EngineTurnLabelPart[] {
    return labelParts(text, refs ?? []).map((part) =>
        typeof part === "string"
            ? { kind: "words" as const, text: part }
            : {
                  kind: "ref" as const,
                  ref: {
                      text: part.text,
                      chips: chipsFor(part, context),
                      ...(part.ref ? { entity: part.ref } : {}),
                  },
              },
    );
}

function stepItem(
    row: GroupedRow,
    currentId: string | null,
    phase: TurnPhase,
    context: BuildContext,
): EngineTurnStep {
    const { step } = row;
    if (step.id === WAITING_ROW_ID) {
        return {
            kind: "step",
            id: step.id,
            op: "wait",
            status: "waiting",
            text: WAITING_TEXT,
            label: [{ kind: "words", text: WAITING_TEXT }],
            times: 1,
            current: true,
        };
    }
    const current = step.id === currentId;
    const running = current && step.status === "running";
    const text = rowText(step);
    const phrase = rowHelperPhrase(step);
    const clock = context.input.clock;
    return {
        kind: "step",
        id: step.id,
        op: step.op,
        status: step.status,
        text,
        label: labelOf(text, step.refs, context),
        ...(row.result ? { result: row.result } : {}),
        times: row.times,
        ...(row.times > 1 ? { timesText: timesText(row.times) } : {}),
        current,
        ...(running
            ? { still: { format: stillFormat(step.label), fromSeconds: STILL_FROM_SECONDS } }
            : {}),
        ...(running && phase === "live" && clock ? { since: rowSince(clock, step.id) } : {}),
        ...(step.helper ? { helper: { name: step.helper, ...(phrase ? { phrase } : {}) } } : {}),
        ...(step.status === "failed" && step.error ? { error: step.error } : {}),
    };
}

function planCard(
    plan: PlanModel,
    turn: TurnModel,
    phase: TurnPhase,
    context: BuildContext,
): EngineTurnPlan {
    const lanes = turn.lanes;
    const built = builtText(
        turn.steps.find((step) => step.op === "write" && isCountedStep(step))?.label,
    );
    const live = phase === "live";
    // A live card counts its own clock (" · 2:10"); a finished one says how long it worked.
    const status = deepHeaderText(plan, lanes, phase, live ? null : planDuration(plan, 0));
    const since = live ? context.input.clock?.startedAt : undefined;
    return {
        plan,
        lanes,
        live,
        ...status,
        ...(since !== undefined ? { clock: { since, format: HEADER_CLOCK_FORMAT } } : {}),
        scope: `${plan.total} ${shortNoun(plan.noun)} in ${plan.groups.length} groups`,
        ...planSummary(plan, lanes),
        rows: planRows(plan).map((row): EngineTurnPlanRow => {
            if (row.kind === "waiting") {
                return {
                    kind: "waiting",
                    from: row.from,
                    to: row.to,
                    text: waitingRunText(plan, row),
                    meta: "waiting",
                };
            }
            const { group } = row;
            const lane = lanes.find((candidate) => candidate.group === group.index);
            const action = group.status === "reading" ? currentAction(plan, lane) : null;
            const worker = workerText(group);
            return {
                kind: "group",
                index: group.index,
                text: groupText(group),
                status: group.status,
                ...(worker ? { worker } : {}),
                ...(action ? { action } : {}),
                meta: groupMeta(plan, group, lane),
            };
        }),
        tail: deepTailRows(plan, built),
    };
}

function stepListItems(
    turn: TurnModel,
    phase: TurnPhase,
    context: BuildContext,
): { items: EngineTurnItem[]; window?: EngineTurnSegment["window"] } {
    const rows = turnRows(turn);
    const grouped = context.input.groupRepeats
        ? groupRepeatedSteps(rows)
        : rows.map((step) => ({ step, times: 1, result: rowResult(step) }));
    const currentId = currentRowId(turn);
    const items = grouped.map((row): EngineTurnItem => {
        const { step } = row;
        if (step.kind === "narration") {
            return {
                kind: "narration",
                id: step.id,
                text: step.label,
                label: labelOf(step.label, step.refs, context),
            };
        }
        if (step.kind === "plan" && step.plan) {
            return {
                kind: "card",
                id: step.id,
                card: { kind: "plan", plan: planCard(step.plan, turn, phase, context) },
            };
        }
        return stepItem(row, currentId, phase, context);
    });
    const { hidden, cut } = stepWindow(
        grouped.map((row) => row.step),
        false,
    );
    return {
        items,
        ...(hidden > 0
            ? {
                  window: {
                      firstShown: cut,
                      hidden,
                      text: `Show ${hidden} earlier ${hidden === 1 ? "step" : "steps"}`,
                  },
              }
            : {}),
    };
}

function headerOf(turn: TurnModel, phase: TurnPhase, context: BuildContext): EngineTurnHeader {
    const live = phase === "live";
    const ended = phase === "cancelled" || phase === "error";
    const counted = countedSteps(turn.steps);
    const { lead, title } = turnLead(phase, turn);
    const clock = turnClock(phase, turn);
    const turnClockOf = context.input.clock;
    const since = (live || ended) && turnClockOf ? turnClockOf.startedAt : undefined;
    const endedAt = ended ? turnClockOf?.endedAt : undefined;
    const plan = turn.plan;
    return {
        shown: plan ? true : headerShown(phase, turn),
        op: headOp(phase, turn),
        lead,
        title,
        tone: turn.waiting && live ? "waiting" : live ? "live" : ended ? "ended" : "done",
        stepCount: counted,
        ...(counted > 0 ? { stepCountText: stepCountText(counted) } : {}),
        clock: {
            ...clock,
            fromSeconds: CLOCK_FROM_SECONDS,
            ...(since !== undefined ? { since } : {}),
            ...(endedAt !== undefined ? { endedAt } : {}),
            format: HEADER_CLOCK_FORMAT,
        },
        expandable: plan ? true : turnRows(turn).length > 0,
        open: openByDefault(phase, turn.hasText),
        ...(plan ? { deep: deepHeaderText(plan, turn.lanes, phase, planDuration(plan, 0)) } : {}),
    };
}

/** The answer's words and cards, in stream order, as items (the steps live in the header's list). */
function contentItems(
    renderItems: RenderItem[],
    phase: TurnPhase,
    writing: boolean,
    context: BuildContext,
): EngineTurnItem[] {
    const items: EngineTurnItem[] = [];
    const lastText = [...renderItems].reverse().find((item) => item.kind === "text");
    // A card's words as the answer's markdown reads, citations as links.
    const fallback = (content: string) =>
        linkedRenderSegments(
            preprocessMessageMarkdown(content, false),
            context.input.referenceMap,
            context.input.files,
            context.input.resolver,
        )
            .flatMap((segment) => (segment.kind === "markdown" ? [segment.content] : []))
            .join("")
            .trim();
    const pushText = (id: string, content: string, streaming: boolean) => {
        const segments = linkedRenderSegments(
            preprocessMessageMarkdown(content, streaming),
            context.input.referenceMap,
            context.input.files,
            context.input.resolver,
        );
        segments.forEach((segment, index) => {
            const at = `${id}${index === 0 ? "" : `-${index}`}`;
            if (segment.kind === "markdown") {
                if (segment.content.trim().length === 0) return;
                items.push({ kind: "text", id: at, markdown: segment.content, streaming });
            } else if (segment.directive.type === "dig_deeper") {
                // Deep research offered after the answer: its own card. Other widgets the server
                // still sends this client are dropped, never shown as JSON.
                items.push({
                    kind: "card",
                    id: at,
                    card: {
                        kind: "deep_research_offer",
                        offer: segment.directive,
                        words: deepResearchOfferWords(segment.directive),
                    },
                });
            } else if (segment.directive.type === "drawing_view") {
                const drawing = drawingView(segment.directive, context);
                if (drawing)
                    items.push({ kind: "card", id: at, card: { kind: "drawing_view", drawing } });
            }
        });
    };
    for (const item of renderItems) {
        switch (item.kind) {
            case "text":
                pushText(item.id, item.content, phase === "live" && writing && item === lastText);
                break;
            case "question":
                items.push({
                    kind: "card",
                    id: item.id,
                    card: { kind: "question", question: item.question },
                    fallback: fallback(questionMarkdown(item.question)),
                });
                break;
            case "action":
                items.push({
                    kind: "card",
                    id: item.id,
                    card: actionCard(item.action),
                    fallback: fallback(actionMarkdown(item.action)),
                });
                break;
            case "navigation":
                items.push({
                    kind: "card",
                    id: item.id,
                    card: { kind: "navigation", navigation: withHref(item.suggestion) },
                });
                break;
            case "artifact-image":
            case "artifact-inline-html":
            case "artifact-panel":
                items.push({
                    kind: "card",
                    id: item.id,
                    card: {
                        kind: "artifact",
                        artifact: item.artifact,
                        presentation:
                            item.kind === "artifact-image"
                                ? "inline-image"
                                : item.kind === "artifact-inline-html"
                                  ? "inline-html"
                                  : "panel",
                        name: artifactDisplayName(item.artifact),
                    },
                });
                break;
            default:
                break;
        }
    }
    return items;
}

/**
 * The place a `drawing_view` widget's citation names, with the link its chip opens. None when the
 * message carries no such citation or not its file, as its chip would not show either.
 */
function drawingView(
    widget: MarvinDrawingViewWidget,
    context: BuildContext,
): EngineTurnDrawingView | null {
    const key = String(widget.reference_index);
    const reference = context.processor.getReferenceOrNull(key);
    const file = reference ? context.filesById.get(reference.fileId) : undefined;
    if (!reference || !file) return null;
    // The citation's first box, as its chip's highlight reads it (`referenceHref`).
    const bounds = (Object.values(reference.bounds ?? {}) as ApiBounds[])[0] ?? FULL_PAGE_BOUNDS;
    const { code, titles } = readDrawingPath(reference.sectionPath, file);
    const href = referenceHref(
        reference,
        file,
        context.processor.getDescription(key),
        context.input.resolver,
    );
    return {
        code,
        title: titles.join(" · "),
        fileId: reference.fileId,
        page: bounds.page,
        bounds: { xMin: bounds.xMin, yMin: bounds.yMin, xMax: bounds.xMax, yMax: bounds.yMax },
        wholePage: isFullPageBounds(bounds),
        ...(href ? { href } : {}),
        ...(reference.sectionUuid ? { sectionUuid: reference.sectionUuid } : {}),
    };
}

/**
 * A navigation card with the app path it opens: the server's `route.href`, else (a card that
 * reached the client without one) the web's own spelling of the route's ids. A route that names no
 * path keeps none, and native draws the card's label as plain words.
 */
function withHref(suggestion: MarvinNavigationSuggestion): MarvinNavigationSuggestion {
    if (suggestion.route.href) return suggestion;
    const href = marvinNavigationUrl(suggestion.kind, suggestion.route);
    return href ? { ...suggestion, route: { ...suggestion.route, href } } : suggestion;
}

/**
 * An action's card: Marvin's markups have their own once they are saved (the result names them); any
 * other action, and a markup_save still waiting for approval or that failed, reads its details and
 * outcomes, with Approve / Reject while it waits (the web's MARKUP_CARD_RENDERERS falls back alike).
 */
function actionCard(action: MarvinAction): EngineTurnCard {
    if (action.actionType === MARKUP_ACTION_TYPE) {
        const markup = markupCardView(action);
        if (markup) return { kind: "markup", action, markup };
    }
    const draft = draftCardView(action);
    return {
        kind: "action",
        action,
        view: {
            details: actionDetails(action),
            outcomes: actionOutcomes(action),
            decisionError: ACTION_DECISION_ERROR,
            inertText: ACTION_INERT_TEXT,
            ...(draft ? { draft } : {}),
        },
    };
}

/** Consecutive text parts as one run of markdown, so a list or table split across parts reads whole. */
function joinedText(items: RenderItem[]): RenderItem[] {
    const joined: RenderItem[] = [];
    for (const item of items) {
        const last = joined[joined.length - 1];
        if (item.kind === "text" && last?.kind === "text") {
            joined[joined.length - 1] = { ...last, content: last.content + item.content };
        } else {
            joined.push(item);
        }
    }
    return joined;
}

/** The turn view model of one Marvin message or live run, from its parts. */
export function buildEngineTurn(input: EngineTurnInput): EngineTurn {
    if (input.phase !== "live" && input.clock && input.clock.endedAt === undefined) {
        input.clock.endedAt = input.clock.now();
    }
    const processor = new ConversationReferenceProcessor(
        new Map(Object.entries(input.referenceMap)),
        input.files,
    );
    const context: BuildContext = {
        processor,
        filesById: new Map(input.files.map((file) => [file.id, file])),
        input,
    };
    const resolve = (text: string) => resolveThinkingReferences(stripBold(text), processor);
    const segments = buildTurnSegments(input.parts, input.phase, resolve);
    const renderItems = joinedText(
        buildRenderItems(input.parts).filter(
            (item) => item.kind !== "activity" && item.kind !== "helper" && item.kind !== "memory",
        ),
    );
    const lastSegment = segments[segments.length - 1];
    const writing = lastSegment?.turn.writing ?? false;
    const content = contentBySegment(renderItems, segments.length).map((group, index) =>
        contentItems(
            group,
            index === segments.length - 1 ? input.phase : "complete",
            index === segments.length - 1 && writing,
            context,
        ),
    );
    const words = turnEndWords(
        input.phase,
        segments.some((segment) => segment.turn.hasText),
        segments.some((segment) => segment.turn.steps.length > 0),
    );
    const endedAt = input.clock?.endedAt;
    return {
        phase: input.phase,
        ...(input.runId ? { runId: input.runId } : {}),
        ...(input.conversationId ? { conversationId: input.conversationId } : {}),
        ...(input.messageId ? { messageId: input.messageId } : {}),
        segments: segments.map((segment, index): EngineTurnSegment => {
            const steps = stepListItems(segment.turn, segment.phase, context);
            return {
                id: `segment-${index}`,
                phase: segment.phase,
                header: headerOf(segment.turn, segment.phase, context),
                items: [...steps.items, ...(content[index] ?? [])],
                stepListLength: steps.items.length,
                ...(steps.window ? { window: steps.window } : {}),
            };
        }),
        ...(words ? { end: { ...words, ...(endedAt !== undefined ? { at: endedAt } : {}) } } : {}),
    };
}

/** A run's or saved message's phase, from its status ("streaming" and "creating" are live). */
export function turnPhaseOf(status: string | undefined): TurnPhase {
    if (status === "streaming" || status === "creating") return "live";
    if (status === "cancelled") return "cancelled";
    return status === "error" ? "error" : "complete";
}

/**
 * The turn of a saved Marvin message, from its parts and their citations (the message's own,
 * then the reference parts'). A message without parts (a legacy one) has none: native draws its
 * markdown segments.
 */
export function savedMessageTurn(
    parts: ApiMessagePart[],
    status: string | undefined,
    references: Record<string, ApiReference>,
    files: ApiFile[],
    resolver: ConversationReferenceResolver | undefined,
    run: { runId?: string; conversationId?: string; messageId?: string } = {},
): EngineTurn | undefined {
    if (parts.length === 0) return undefined;
    const partFiles = partsFiles(parts);
    const known = new Set(partFiles.map((file) => file.id));
    return buildEngineTurn({
        parts,
        phase: turnPhaseOf(status),
        // A `model` reference cites no file: native draws no link for its `{N}`.
        referenceMap: { ...references, ...fileReferences(partsReferenceMap(parts)) },
        files: [...partFiles, ...files.filter((file) => !known.has(file.id))],
        resolver,
        groupRepeats: true,
        ...run,
    });
}
